import React, { useState, useMemo } from 'react'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import MuiButton from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import { Link, useLocation } from 'react-router-dom'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { createDefaultPaginationData, formatCount, formatMoney, createTableData } from '../../../utils/default/TableHelper'
import { PaginationRes, ActivityInviteFriend, ActivityInviteFriendSum, ActivityInviteFriendQuery, PaginationReq, ActivityType, ActivityInviteFriendRelation, ActivityInviteFriendRelations } from '@golden/gdk-admin'
import { ActivityInviteFriendStatusType, InviteFriendStatus, InviteFriendTask } from '@golden/gdk-campaign'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { searchToRequest } from './ActivityRecordDetailForm'
import { useRequestFromSearch, useGetDataByPayload, usePaginationClickAndChangeUrl } from '../../../utils/default/ComplexFlowHook'
import DateTime from '../../default/present/DateTime'
import ScrollablePaper from '../../default/present/ScrollablePaper'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'
import CoreTable from '../../default/present/CoreTable'
import allRoute, { Path } from '../route/route'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import { inviteFriendStatusName } from '../../../constants/default/activityStatusName'
import { inviteFriendStatusColor } from '../../../constants/default/activityStatusColor'
import TKeyType from '../../../i18ns/admin/TKeyType'

const inviteeFriendStatusNameMap: { [key in InviteFriendStatus]: TKeyType } = {
  pending: 'common.inviteeFriendStatusPending',
  valid: 'common.inviteeFriendStatusValid',
  invalid: 'common.inviteeFriendStatusInvalid',
  banned: 'common.inviteeFriendStatusBanned',
  old_activity: 'common.inviteeFriendStatusOldActivity'
}

const inviteeFriendTaskNameMap: { [key in InviteFriendTask]: TKeyType } = {
  'log-in-app': 'common.inviteeFriendTaskLogInApp',
  'first-deposit': 'common.inviteeFriendTaskReachMinDeposit',
  'reach-min-turnover': 'common.inviteeFriendTaskReachMinTurnover'
}

interface SumRowType {
  id: number
  status: string
  count: string
  points: string
}

interface RowType {
  id: number
  order: string
  applyTime: React.ReactElement
  account: string
  friendAccounts: string
  staffAccount: string
  content: string
  status: React.ReactElement
  bonus: string
  updatedAt: React.ReactElement
  updatedBy: string
}

interface RelationRowType {
  id: number
  account: React.ReactElement
  status: string
  tasks: string
  registerDays: number | string
  rank: string
}

interface InviteRecordDialogProps {
  open: boolean
  onClose: () => void
  account: string
}

const defaultRelations: ActivityInviteFriendRelations = {
  summary: { total_count: 0, valid_count: 0 },
  data: []
}

const InviteRecordDialog: React.FC<InviteRecordDialogProps> = (props) => {
  const { open, onClose, account } = props
  const { t } = useT()
  const classes = useCommonStyles()
  const gdk = useGDK()
  const [relations, setRelations] = useState<ActivityInviteFriendRelations>(defaultRelations)
  const pageFlow = usePageFlow()

  useGetDataByPayload({
    payload: account,
    gdkFunc: (acc: string) => gdk.activity.getActivityRecordInviteFriendRelations(acc),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: ActivityInviteFriendRelations) => {
      setRelations(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: open && account !== ''
  })

  const tableClasses = useMemo(() => ({
    head: classes.greyTableHead,
    row: classes.tableRow,
    cellHead: classes.tableCellHead,
    cellBody: classes.nowrap
  }), [classes])

  const rows = useMemo(() => {
    return relations.data.map((item: ActivityInviteFriendRelation, index: number) => ({
      id: index,
      account: (
        <Link
          target="_blank"
          to={allRoute.playerDetail.encodePath({ search: {}, param: { id: item.invitee_id } })}
        >
          {item.invitee_account}
        </Link>
      ),
      status: t(inviteeFriendStatusNameMap[item.status]),
      tasks: item.tasks.length === 0
        ? '-'
        : item.tasks.map((task) => t(inviteeFriendTaskNameMap[task])).join('、'),
      registerDays: (item.status === 'valid' || item.status === 'pending') ? item.register_days : '-',
      rank: (item.status === 'pending' || item.status === 'valid') && item.invitee_rank ? t(`player.${item.invitee_rank}` as TKeyType) : '-'
    } as RelationRowType))
  }, [relations.data, classes, t])

  const data = useMemo(() => {
    return createTableData<RelationRowType>(
      {
        id: { label: '', value: 'id' },
        account: { label: t('common.playerAccount'), value: 'account', align: 'center' },
        status: { label: t('common.status'), value: 'status', align: 'center' },
        tasks: { label: t('common.activityContent'), value: 'tasks', align: 'center' },
        registerDays: { label: t('common.inviteRegisterDays'), value: 'registerDays', align: 'center' },
        rank: { label: t('common.inviteCurrentRank'), value: 'rank', align: 'center' }
      },
      ['account', 'status', 'tasks', 'registerDays', 'rank'],
      rows,
      'id'
    )
  }, [t, rows])

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <Box paddingY={1.25} paddingX={2} className={classes.pinkTitleBar}>
        <Typography variant="h5">{t('common.inviteRecord')}</Typography>
      </Box>
      <DialogContent>
        <LoadingAndErrorFrame {...pageFlow.status}>
          <Box paddingY={1}>
            <Typography variant="body1">
              {t('common.inviteTotalCount')}：{formatCount(relations.summary.total_count)}
              {'　'}
              {t('common.inviteValidCount')}：{formatCount(relations.summary.valid_count)}
            </Typography>
          </Box>
          <Box style={{ maxHeight: 420, overflowY: 'auto' }}>
            <CoreTable
              data={data}
              total={relations.data.length}
              classes={tableClasses}
            />
          </Box>
        </LoadingAndErrorFrame>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} color="primary" variant="contained">
          {t('common.close')}
        </MuiButton>
      </DialogActions>
    </Dialog>
  )
}

const ActivityRecordInviteFriendTable: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const activities = useGDKStore.activity.activities()
  const location = useLocation()
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [list, setList] = useState<PaginationRes<ActivityInviteFriend[]> & { summary: ActivityInviteFriendSum }>({
    ...createDefaultPaginationData([]),
    summary: {
      init: { total_count: 0, total_bonus: '0.0000' },
      claimed: { total_count: 0, total_bonus: '0.0000' },
      expired: { total_count: 0, total_bonus: '0.0000' }
    }
  })
  const pageFlow = usePageFlow()
  const request = useRequestFromSearch({ searchToRequest })
  useGetDataByPayload({
    payload: request as ActivityInviteFriendQuery & PaginationReq,
    gdkFunc: (payload) => gdk.activity.getActivityRecordInviteFriend(payload),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: PaginationRes<ActivityInviteFriend[]> & { summary: ActivityInviteFriendSum }) => {
      setList(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: request !== undefined
  })

  const isSingleAccountSearch = useMemo(() => {
    return !!(request?.account) && request.account !== ''
  }, [request])

  const tableClasses = useMemo(() => ({
    head: classes.greyTableHead,
    row: classes.tableRow,
    cellHead: classes.tableCellHead,
    cellBody: classes.nowrap
  }), [classes])

  const sumRows = useMemo(() => {
    const { summary } = list
    return [
      { id: 1, status: t('activityStatus.notReceived'), count: formatCount(summary.init.total_count), points: formatMoney(String(summary.init.total_bonus)) },
      { id: 2, status: t('activityStatus.received'), count: formatCount(summary.claimed.total_count), points: formatMoney(String(summary.claimed.total_bonus)) },
      { id: 3, status: t('activityStatus.expiredSimple'), count: formatCount(summary.expired.total_count), points: formatMoney(String(summary.expired.total_bonus)) }
    ] as SumRowType[]
  }, [list, t])

  const sumData = useMemo(() => {
    return createTableData<SumRowType>(
      {
        id: { label: '', value: 'id' },
        status: { label: t('common.status'), value: 'status', align: 'center' },
        count: { label: t('common.inviteFriendRecordCount'), value: 'count', align: 'right' },
        points: { label: t('common.inviteFriendRecordPoints'), value: 'points', align: 'right' }
      },
      ['status', 'count', 'points'],
      sumRows,
      'id'
    )
  }, [t, sumRows])

  const rows = useMemo(() => {
    return list.data.map((item, index) => ({
      id: index,
      order: item.serial_number,
      applyTime: (<DateTime time={item.created_at} />),
      account: item.account,
      friendAccounts: item.friend_accounts.join(', '),
      staffAccount: item.staff_account,
      content: item.content,
      status: (
        <p
          className={classes.chipText}
          style={{ backgroundColor: inviteFriendStatusColor[item.status] }}
        >
          {t(inviteFriendStatusName[item.status])}
        </p>
      ),
      bonus: formatMoney(item.bonus),
      updatedAt: item.updated_at ? (<DateTime time={item.updated_at} />) : (<p></p>),
      updatedBy: item.updated_by
    } as RowType))
  }, [list.data, t, classes])

  const data = useMemo(() => {
    return createTableData<RowType>(
      {
        id: { label: '', value: 'id' },
        order: { label: t('common.activitySerial'), value: 'order', align: 'center' },
        applyTime: { label: t('common.applyTime'), value: 'applyTime', align: 'center' },
        account: { label: t('common.inviter'), value: 'account', align: 'center' },
        friendAccounts: { label: t('common.inviteeFriendAccount'), value: 'friendAccounts', align: 'center' },
        staffAccount: { label: t('common.inviterAgent'), value: 'staffAccount', align: 'center' },
        content: { label: t('common.activityContent'), value: 'content', align: 'center' },
        status: { label: t('common.status'), value: 'status', align: 'center' },
        bonus: { label: t('common.activityBonus'), value: 'bonus', align: 'right' },
        updatedAt: { label: t('common.updatedAt'), value: 'updatedAt', align: 'center' },
        updatedBy: { label: t('common.updateBy'), value: 'updatedBy', align: 'center' }
      },
      ['order', 'applyTime', 'account', 'staffAccount', 'friendAccounts', 'content', 'status', 'bonus', 'updatedAt', 'updatedBy'],
      rows,
      'id'
    )
  }, [t, rows])

  const handlePagination = usePaginationClickAndChangeUrl({
    request,
    encodePath: (location.pathname === Path.ACTIVITY_RECORD_DETAIL) ? allRoute.activityRecordDetail.encodePath : allRoute.activityRecordSub.encodePath
  })

  return (
    <React.Fragment>
      <Box paddingBottom={3}>
        <ScrollablePaper marginX={5}>
          <Box padding={5}>
            <LoadingAndErrorFrame {...pageFlow.status}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                paddingY={1.25}
                paddingX={2}
                className={classes.pinkTitleBar}
              >
                <Typography variant="h5">
                  {activities.find((item) => item.type === ActivityType.INVITE_FRIEND)?.name ?? ''}
                </Typography>
                {isSingleAccountSearch && (
                  <MuiButton
                    className={classes.purpleGradualButton}
                    onClick={() => { setInviteDialogOpen(true) }}
                  >
                    {t('common.inviteRecord')}
                  </MuiButton>
                )}
              </Box>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                  <CoreTable
                    data={sumData}
                    total={3}
                    classes={tableClasses}
                  />
                </Grid>
              </Grid>
            </LoadingAndErrorFrame>
          </Box>
        </ScrollablePaper>
      </Box>
      <ScrollablePaper marginX={5}>
        <Box padding={5}>
          <LoadingAndErrorFrame {...pageFlow.status}>
            <CoreTable
              data={data}
              total={list.total}
              classes={tableClasses}
              showPagination
              page={request?.page ?? 1}
              onChangePage={handlePagination}
            />
          </LoadingAndErrorFrame>
        </Box>
      </ScrollablePaper>
      <InviteRecordDialog
        open={inviteDialogOpen}
        onClose={() => { setInviteDialogOpen(false) }}
        account={request?.account ?? ''}
      />
    </React.Fragment>
  )
}

export default React.memo(ActivityRecordInviteFriendTable)
